import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  Calendar,
  Clock,
  User,
  FileText,
  Loader2,
  Pencil,
  X,
  Trash2,
  ListChecks,
  Lock,
  Eye,
  CalendarPlus,
} from 'lucide-react';
import Button from '../../components/shared/Button';
import Input from '../../components/shared/Input';
import CustomSelect from '../../components/shared/CustomSelect';
import { DatePicker } from '../../components/client';
import { useToastContext } from '../../contexts/ToastContext';
import ModalShell from '../../components/shared/ModalShell';
import { useMyServices } from '../../services/vendorServiceService';
import { useMyServicePackages } from '../../services/vendorServicePackageService';
import {
  useVendorPrivateBookings,
  useCreateVendorPrivateBooking,
  useUpdateVendorPrivateBooking,
  useDeleteVendorPrivateBooking,
  useCreatePrivateBookingInvoice,
} from '../../services/vendorPrivateBookingService';
import { formatDate, formatTime } from '../../utils/dateUtils';
import { VendorPageHeader, VendorGuideCallout } from '../../components/vendor/guidance';
import { VENDOR_PAGE_GUIDES } from '../../utils/vendorPageGuides';

const STATUS_OPTIONS = [
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'pending', label: 'Pending' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
];

const parseApiFieldErrors = (err) => {
  const errors = err?.response?.data?.errors;
  if (!errors || typeof errors !== 'object') return {};
  const fieldErrors = {};
  Object.entries(errors).forEach(([key, messages]) => {
    const msg = Array.isArray(messages) ? messages[0] : messages;
    if (msg) fieldErrors[key] = String(msg);
  });
  return fieldErrors;
};

const getApiErrorMessage = (err, fallback, fieldErrors = {}) => {
  const first = Object.values(fieldErrors)[0];
  if (first) return first;
  return err?.response?.data?.message || fallback;
};

const emptyForm = () => ({
  guest_name: '',
  guest_email: '',
  guest_phone: '',
  manual_title: '',
  use_catalog: true,
  service_id: '',
  package_id: '',
  custom_service_name: '',
  event_date: '',
  start_time: '10:00',
  end_time: '12:00',
  duration_hours: '',
  guest_count: '',
  event_type: 'Private',
  event_location: '',
  event_address: '',
  special_requirements: '',
  vendor_notes: '',
  subtotal_amount: '',
  amount_paid: '',
  status: 'confirmed',
});

const getStatusBadge = (status) => {
  const styles = {
    pending:
      'bg-amber-50 text-amber-800 border-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-800/50',
    confirmed:
      'bg-emerald-50 text-emerald-800 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800/50',
    in_progress:
      'bg-sky-50 text-sky-800 border-sky-200 dark:bg-sky-950/40 dark:text-sky-300 dark:border-sky-800/50',
    completed:
      'bg-gray-100 text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700',
    cancelled:
      'bg-red-50 text-red-700 border-red-200 dark:bg-red-950/40 dark:text-red-300 dark:border-red-800/50',
  };
  return (
    <span
      className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-medium capitalize border ${
        styles[status] || styles.completed
      }`}
    >
      {status?.replace('_', ' ')}
    </span>
  );
};

const VendorPrivateBookings = () => {
  const navigate = useNavigate();
  const { success, error: showError } = useToastContext();
  const [activeTab, setActiveTab] = useState('list');
  const [form, setForm] = useState(emptyForm());
  const [fieldErrors, setFieldErrors] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [invoiceModal, setInvoiceModal] = useState(null);
  const [invoiceDueDate, setInvoiceDueDate] = useState('');
  const [invoiceTaxRate, setInvoiceTaxRate] = useState('');

  const { data: listRes, isLoading } = useVendorPrivateBookings();
  const bookings = listRes?.data?.data || [];

  const { data: servicesRes } = useMyServices({ is_active: 1 });
  const services = servicesRes?.data?.data || servicesRes?.data || [];

  const { data: packagesRes } = useMyServicePackages(
    { service_id: form.service_id, per_page: 100 },
    { enabled: form.use_catalog && !!form.service_id }
  );
  const allPackages = useMemo(() => {
    const raw = packagesRes?.data?.data ?? packagesRes?.data ?? [];
    return Array.isArray(raw) ? raw : [];
  }, [packagesRes]);

  const serviceOptions = useMemo(
    () => services.map((s) => ({ value: s.id, label: s.service_name })),
    [services]
  );

  const packageOptions = useMemo(() => {
    if (!form.service_id) return [];
    return allPackages
      .filter((p) => p.service_id === form.service_id)
      .map((p) => ({
        value: p.id,
        label: `${p.package_name} — $${Number(p.base_price || 0).toFixed(2)}`,
      }));
  }, [allPackages, form.service_id]);

  const createMutation = useCreateVendorPrivateBooking({
    onSuccess: () => {
      success('Booking created', 'Private booking created. It will appear on your calendar.');
      setEditingId(null);
      setForm(emptyForm());
      setFieldErrors({});
      setActiveTab('list');
    },
    onError: (err) => {
      const parsed = parseApiFieldErrors(err);
      if (!form.use_catalog && parsed.service_id) {
        parsed.custom_service_name = parsed.custom_service_name || parsed.service_id;
        delete parsed.service_id;
      }
      setFieldErrors(parsed);
      showError('Could not create booking', getApiErrorMessage(err, 'Please check the form.', parsed));
    },
  });

  const updateMutation = useUpdateVendorPrivateBooking({
    onSuccess: () => {
      success('Booking updated', 'Private booking saved successfully.');
      setEditingId(null);
      setForm(emptyForm());
      setFieldErrors({});
      setActiveTab('list');
    },
    onError: (err) => {
      const parsed = parseApiFieldErrors(err);
      if (!form.use_catalog && parsed.service_id) {
        parsed.custom_service_name = parsed.custom_service_name || parsed.service_id;
        delete parsed.service_id;
      }
      setFieldErrors(parsed);
      showError('Update failed', getApiErrorMessage(err, 'Please try again.', parsed));
    },
  });

  const deleteMutation = useDeleteVendorPrivateBooking({
    onSuccess: () => {
      success('Booking deleted', 'Private booking removed from your calendar.');
      if (editingId) {
        setEditingId(null);
        setForm(emptyForm());
      }
    },
    onError: (err) => {
      showError('Delete failed', err?.response?.data?.message || 'Could not delete this booking.');
    },
    onSettled: () => setDeletingId(null),
  });

  const invoiceMutation = useCreatePrivateBookingInvoice({
    onSuccess: (res) => {
      success('Invoice created', 'Your invoice has been created.');
      setInvoiceModal(null);
      const id = res?.data?.id;
      if (id) navigate(`/vendor/invoices/${id}`);
    },
    onError: (err) => {
      showError('Invoice failed', err?.response?.data?.message || 'Could not create invoice.');
    },
  });

  useEffect(() => {
    if (!form.use_catalog || !form.package_id) return;
    const pkg = allPackages.find((p) => p.id === form.package_id);
    if (!pkg) return;
    setForm((prev) => ({
      ...prev,
      subtotal_amount: prev.subtotal_amount || String(pkg.base_price ?? ''),
      duration_hours: prev.duration_hours || String(pkg.duration_hours ?? ''),
    }));
  }, [form.package_id, form.use_catalog, allPackages]);

  const handleChange = (field, value) => {
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'service_id') {
        next.package_id = '';
      }
      if (field === 'use_catalog' && value) {
        next.custom_service_name = '';
      }
      if (field === 'use_catalog' && !value) {
        next.service_id = '';
        next.package_id = '';
      }
      return next;
    });
    if (fieldErrors[field]) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        if (field === 'use_catalog') {
          delete next.service_id;
          delete next.custom_service_name;
        }
        return next;
      });
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!form.guest_name.trim()) {
      errors.guest_name = 'Client name is required.';
    }
    if (form.guest_email && !/\S+@\S+\.\S+/.test(form.guest_email)) {
      errors.guest_email = 'Enter a valid email address.';
    }
    if (!form.event_date) {
      errors.event_date = 'Event date is required.';
    }
    if (form.use_catalog) {
      if (!form.service_id) {
        errors.service_id = 'Select a service from your catalog or switch to custom entry.';
      }
    } else {
      if (!form.custom_service_name.trim()) {
        errors.custom_service_name = 'Enter a name for this booking.';
      }
      if (form.subtotal_amount === '' || form.subtotal_amount == null) {
        errors.subtotal_amount = 'Amount is required for custom entries.';
      }
    }
    if (form.start_time && form.end_time && form.end_time <= form.start_time) {
      errors.end_time = 'End time must be after start time.';
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const buildPayload = () => {
    const payload = { ...form };
    delete payload.use_catalog;

    if (form.use_catalog) {
      delete payload.custom_service_name;
    } else {
      delete payload.service_id;
      delete payload.package_id;
      payload.custom_service_name = form.custom_service_name.trim();
      payload.custom_package_name = null;
    }

    ['duration_hours', 'guest_count', 'subtotal_amount', 'amount_paid'].forEach((key) => {
      if (payload[key] === '' || payload[key] == null) {
        delete payload[key];
      } else {
        payload[key] = Number(payload[key]);
      }
    });

    return payload;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) {
      showError('Please fix the form', 'Some fields need your attention before saving.');
      return;
    }
    const payload = buildPayload();
    if (editingId) {
      updateMutation.mutate({ id: editingId, data: payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  const startEdit = (booking) => {
    const useCatalog = !booking.custom_service_name && !!booking.service_id;
    const customName = booking.custom_service_name
      ? booking.custom_package_name
        ? `${booking.custom_service_name} — ${booking.custom_package_name}`
        : booking.custom_service_name
      : '';
    setEditingId(booking.id);
    setFieldErrors({});
    setForm({
      guest_name: booking.guest_name || '',
      guest_email: booking.guest_email || '',
      guest_phone: booking.guest_phone || '',
      manual_title: booking.manual_title || '',
      use_catalog: useCatalog,
      service_id: booking.service_id || '',
      package_id: booking.package_id || '',
      custom_service_name: customName,
      event_date: booking.event_date?.slice?.(0, 10) || booking.event_date || '',
      start_time: booking.start_time?.slice?.(11, 16) || booking.start_time?.slice?.(0, 5) || '10:00',
      end_time: booking.end_time?.slice?.(11, 16) || booking.end_time?.slice?.(0, 5) || '12:00',
      duration_hours: booking.duration_hours != null ? String(booking.duration_hours) : '',
      guest_count: booking.guest_count != null ? String(booking.guest_count) : '',
      event_type: booking.event_type || 'Private',
      event_location: booking.event_location || '',
      event_address: booking.event_address || '',
      special_requirements: booking.special_requirements || '',
      vendor_notes: booking.vendor_notes || '',
      subtotal_amount: booking.subtotal_amount != null ? String(booking.subtotal_amount) : '',
      amount_paid: booking.amount_paid != null ? String(booking.amount_paid) : '',
      status: booking.status || 'confirmed',
    });
    setActiveTab('create');
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(emptyForm());
    setFieldErrors({});
  };

  const openInvoiceModal = (booking) => {
    const due = new Date();
    due.setDate(due.getDate() + 14);
    setInvoiceDueDate(due.toISOString().slice(0, 10));
    setInvoiceTaxRate('');
    setInvoiceModal(booking);
  };

  const submitInvoice = () => {
    if (!invoiceModal) return;
    invoiceMutation.mutate({
      bookingId: invoiceModal.id,
      payload: {
        due_date: invoiceDueDate,
        tax_rate: invoiceTaxRate ? Number(invoiceTaxRate) : 0,
        status: 'draft',
        document_type: 'receipt',
      },
    });
  };

  const handleDelete = (booking) => {
    const label = booking.manual_title || booking.guest_name || booking.booking_reference;
    if (
      !window.confirm(
        `Delete private booking "${label}"? This cannot be undone. Linked invoices will be kept but unlinked from this booking.`
      )
    ) {
      return;
    }
    setDeletingId(booking.id);
    deleteMutation.mutate(booking.id);
  };

  return (
    <div className="space-y-5">
      <VendorPageHeader
        title="Private Bookings"
        subtitle="Log off-platform jobs yourself — they stay private, show on your calendar, and can generate invoices."
        actions={
          activeTab === 'list' ? (
            <Button
              onClick={() => {
                cancelEdit();
                setActiveTab('create');
              }}
              className="!rounded-xl"
            >
              <Plus className="w-4 h-4 mr-1.5" />
              New private booking
            </Button>
          ) : null
        }
      />

      <VendorGuideCallout
        title={VENDOR_PAGE_GUIDES.privateBookings.title}
        steps={VENDOR_PAGE_GUIDES.privateBookings.steps}
        icon={ListChecks}
      />

      <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm overflow-hidden">
        <div className="border-b border-gray-200 dark:border-gray-800 px-4 py-3 sm:px-5">
          <div className="inline-flex rounded-xl bg-gray-100 dark:bg-gray-800 p-1 border border-gray-200/80 dark:border-gray-700">
            {[
              { key: 'list', label: 'My private bookings', icon: Lock },
              {
                key: 'create',
                label: editingId ? 'Edit booking' : 'Create booking',
                icon: CalendarPlus,
              },
            ].map((tab) => {
              const TabIcon = tab.icon;
              const isActive = activeTab === tab.key;
              return (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setActiveTab(tab.key)}
                  className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-medium transition-all ${
                    isActive
                      ? 'bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-50 shadow-sm'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                  }`}
                >
                  <TabIcon className="w-3.5 h-3.5" />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="p-4 sm:p-6">
          {activeTab === 'list' ? (
            isLoading ? (
              <div className="py-20 text-center">
                <Loader2 className="w-8 h-8 animate-spin text-primary-600 mx-auto mb-3" />
                <p className="text-sm text-gray-500 dark:text-gray-400">Loading private bookings…</p>
              </div>
            ) : bookings.length === 0 ? (
              <div className="py-16 sm:py-20 text-center px-4">
                <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-400">
                  <CalendarPlus className="w-7 h-7" />
                </div>
                <h3 className="text-base font-semibold text-gray-900 dark:text-gray-50">
                  No private bookings yet
                </h3>
                <p className="mt-1.5 text-sm text-gray-500 dark:text-gray-400 max-w-sm mx-auto leading-relaxed">
                  Log off-platform jobs here so they appear on your calendar and can be invoiced.
                </p>
                <Button className="mt-5 !rounded-xl" onClick={() => setActiveTab('create')}>
                  <Plus className="w-4 h-4 mr-1.5" />
                  Create your first private booking
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {bookings.map((b) => (
                  <div
                    key={b.id}
                    className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-950/40 p-4 sm:p-5 hover:border-gray-300 dark:hover:border-gray-600 transition-colors"
                  >
                    <div className="flex flex-col lg:flex-row lg:items-start gap-4">
                      <div className="flex-1 min-w-0 space-y-2.5">
                        <div className="flex flex-wrap items-center gap-2">
                          <h3 className="text-sm sm:text-base font-semibold text-gray-900 dark:text-gray-50 truncate">
                            {b.manual_title || b.guest_name || 'Private client'}
                          </h3>
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-gray-900 text-white dark:bg-gray-100 dark:text-gray-900">
                            <Lock className="w-3 h-3" />
                            Private
                          </span>
                          {getStatusBadge(b.status)}
                          <span className="text-xs text-gray-400 dark:text-gray-500 tabular-nums">
                            #{b.booking_reference}
                          </span>
                        </div>
                        <div className="flex flex-wrap gap-x-4 gap-y-1.5 text-sm text-gray-600 dark:text-gray-400">
                          <span className="inline-flex items-center gap-1.5 min-w-0">
                            <User className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                            <span className="truncate">
                              {b.guest_name}
                              {b.guest_email ? ` · ${b.guest_email}` : ''}
                            </span>
                          </span>
                          <span className="inline-flex items-center gap-1.5">
                            <Calendar className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                            {formatDate(b.event_date)}
                          </span>
                          <span className="inline-flex items-center gap-1.5">
                            <Clock className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                            {formatTime(b.start_time)}
                          </span>
                          {(b.custom_service_name || b.vendor_service?.service_name) && (
                            <span className="truncate">
                              {b.custom_service_name || b.vendor_service.service_name}
                            </span>
                          )}
                          {b.subtotal_amount != null && (
                            <span className="font-semibold text-gray-900 dark:text-gray-100 tabular-nums">
                              ${Number(b.subtotal_amount).toFixed(2)}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2 shrink-0">
                        <Button size="sm" variant="outline" className="!rounded-lg" onClick={() => startEdit(b)}>
                          <Pencil className="w-3.5 h-3.5 mr-1" />
                          Edit
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="!rounded-lg"
                          onClick={() => openInvoiceModal(b)}
                        >
                          <FileText className="w-3.5 h-3.5 mr-1" />
                          Invoice
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="!rounded-lg"
                          onClick={() => navigate(`/vendor/bookings/${b.id}`)}
                        >
                          <Eye className="w-3.5 h-3.5 mr-1" />
                          View
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDelete(b)}
                          disabled={deletingId === b.id || deleteMutation.isPending}
                          className="!rounded-lg text-red-600 border-red-200 hover:bg-red-50 dark:border-red-900/50 dark:hover:bg-red-950/30"
                        >
                          <Trash2 className="w-3.5 h-3.5 mr-1" />
                          {deletingId === b.id ? 'Deleting…' : 'Delete'}
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              {Object.keys(fieldErrors).length > 0 && (
                <div className="rounded-xl border border-red-200 dark:border-red-800/50 bg-red-50/80 dark:bg-red-950/30 px-4 py-3 text-sm text-red-700 dark:text-red-300">
                  <p className="font-medium">Please fix the following:</p>
                  <ul className="mt-1 list-disc list-inside space-y-0.5">
                    {Object.values(fieldErrors).map((msg, index) => (
                      <li key={index}>{msg}</li>
                    ))}
                  </ul>
                </div>
              )}

              <section className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/40 dark:bg-gray-950/30 p-4 sm:p-5 space-y-4">
                <div>
                  <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-50">Client details</h2>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    No platform account needed — enter contact info only.
                  </p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Input
                    label="Client name *"
                    value={form.guest_name}
                    onChange={(e) => handleChange('guest_name', e.target.value)}
                    placeholder="Jane Smith"
                    error={fieldErrors.guest_name}
                  />
                  <Input
                    label="Email"
                    type="email"
                    value={form.guest_email}
                    onChange={(e) => handleChange('guest_email', e.target.value)}
                    placeholder="client@email.com"
                    error={fieldErrors.guest_email}
                  />
                  <Input
                    label="Phone"
                    value={form.guest_phone}
                    onChange={(e) => handleChange('guest_phone', e.target.value)}
                    placeholder="+1 555 000 0000"
                    error={fieldErrors.guest_phone}
                  />
                  <Input
                    label="Title / label (optional)"
                    value={form.manual_title}
                    onChange={(e) => handleChange('manual_title', e.target.value)}
                    placeholder="e.g. Smith wedding — photo booth"
                    error={fieldErrors.manual_title}
                  />
                </div>
              </section>

              <section className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/40 dark:bg-gray-950/30 p-4 sm:p-5 space-y-4">
                <div>
                  <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-50">Service &amp; package</h2>
                </div>
                <div className="inline-flex rounded-xl bg-gray-100 dark:bg-gray-800 p-1 border border-gray-200/80 dark:border-gray-700">
                  <button
                    type="button"
                    onClick={() => handleChange('use_catalog', true)}
                    className={`px-3.5 py-1.5 rounded-lg text-sm font-medium transition-all ${
                      form.use_catalog
                        ? 'bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-50 shadow-sm'
                        : 'text-gray-600 dark:text-gray-400'
                    }`}
                  >
                    From my catalog
                  </button>
                  <button
                    type="button"
                    onClick={() => handleChange('use_catalog', false)}
                    className={`px-3.5 py-1.5 rounded-lg text-sm font-medium transition-all ${
                      !form.use_catalog
                        ? 'bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-50 shadow-sm'
                        : 'text-gray-600 dark:text-gray-400'
                    }`}
                  >
                    Custom entry
                  </button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {form.use_catalog ? (
                    <>
                      <CustomSelect
                        label="Service *"
                        options={serviceOptions}
                        value={form.service_id}
                        onChange={(v) => handleChange('service_id', v)}
                        placeholder="Select service"
                        error={fieldErrors.service_id}
                      />
                      <CustomSelect
                        label="Package (optional)"
                        options={packageOptions}
                        value={form.package_id}
                        onChange={(v) => handleChange('package_id', v)}
                        placeholder={form.service_id ? 'Select package' : 'Choose a service first'}
                        disabled={!form.service_id}
                        error={fieldErrors.package_id}
                      />
                    </>
                  ) : (
                    <Input
                      label="Name *"
                      value={form.custom_service_name}
                      onChange={(e) => handleChange('custom_service_name', e.target.value)}
                      placeholder="e.g. Catering booth — 4 hour package"
                      error={fieldErrors.custom_service_name}
                    />
                  )}
                  <Input
                    label={`Amount ($)${!form.use_catalog ? ' *' : ''}`}
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.subtotal_amount}
                    onChange={(e) => handleChange('subtotal_amount', e.target.value)}
                    placeholder={form.use_catalog ? 'Auto-filled from package' : 'Enter amount'}
                    error={fieldErrors.subtotal_amount}
                  />
                  <Input
                    label="Amount paid ($)"
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.amount_paid}
                    onChange={(e) => handleChange('amount_paid', e.target.value)}
                    placeholder="0"
                    error={fieldErrors.amount_paid}
                  />
                </div>
              </section>

              <section className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/40 dark:bg-gray-950/30 p-4 sm:p-5 space-y-4">
                <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-50">Event schedule</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Event date *
                    </label>
                    <DatePicker
                      value={form.event_date}
                      onChange={(d) => handleChange('event_date', d)}
                      placeholder="Select date"
                      error={fieldErrors.event_date}
                    />
                  </div>
                  <Input
                    label="Start time *"
                    type="time"
                    value={form.start_time}
                    onChange={(e) => handleChange('start_time', e.target.value)}
                    error={fieldErrors.start_time}
                  />
                  <Input
                    label="End time *"
                    type="time"
                    value={form.end_time}
                    onChange={(e) => handleChange('end_time', e.target.value)}
                    error={fieldErrors.end_time}
                  />
                  <Input
                    label="Duration (hours)"
                    type="number"
                    min="0.25"
                    step="0.25"
                    value={form.duration_hours}
                    onChange={(e) => handleChange('duration_hours', e.target.value)}
                    error={fieldErrors.duration_hours}
                  />
                  <Input
                    label="Guest count"
                    type="number"
                    min="1"
                    value={form.guest_count}
                    onChange={(e) => handleChange('guest_count', e.target.value)}
                    error={fieldErrors.guest_count}
                  />
                  <CustomSelect
                    label="Status"
                    options={STATUS_OPTIONS}
                    value={form.status}
                    onChange={(v) => handleChange('status', v)}
                    error={fieldErrors.status}
                  />
                </div>
              </section>

              <section className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/40 dark:bg-gray-950/30 p-4 sm:p-5 space-y-4">
                <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-50">Location &amp; notes</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Input
                    label="Event location"
                    value={form.event_location}
                    onChange={(e) => handleChange('event_location', e.target.value)}
                    error={fieldErrors.event_location}
                  />
                  <Input
                    label="Address"
                    value={form.event_address}
                    onChange={(e) => handleChange('event_address', e.target.value)}
                    error={fieldErrors.event_address}
                  />
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Special requirements
                    </label>
                    <textarea
                      className={`w-full border rounded-xl px-3 py-2 text-sm bg-white dark:bg-gray-900 dark:text-gray-100 ${
                        fieldErrors.special_requirements
                          ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                          : 'border-gray-300 dark:border-gray-600'
                      }`}
                      rows={2}
                      value={form.special_requirements}
                      onChange={(e) => handleChange('special_requirements', e.target.value)}
                    />
                    {fieldErrors.special_requirements && (
                      <p className="mt-1 text-sm text-red-600">{fieldErrors.special_requirements}</p>
                    )}
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Your notes (private)
                    </label>
                    <textarea
                      className={`w-full border rounded-xl px-3 py-2 text-sm bg-white dark:bg-gray-900 dark:text-gray-100 ${
                        fieldErrors.vendor_notes
                          ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                          : 'border-gray-300 dark:border-gray-600'
                      }`}
                      rows={2}
                      value={form.vendor_notes}
                      onChange={(e) => handleChange('vendor_notes', e.target.value)}
                    />
                    {fieldErrors.vendor_notes && (
                      <p className="mt-1 text-sm text-red-600">{fieldErrors.vendor_notes}</p>
                    )}
                  </div>
                </div>
              </section>

              <div className="flex flex-wrap gap-3 justify-end pt-2 border-t border-gray-100 dark:border-gray-800">
                <Button
                  type="button"
                  variant="outline"
                  className="!rounded-xl"
                  onClick={() => {
                    cancelEdit();
                    setActiveTab('list');
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="!rounded-xl"
                  loading={createMutation.isPending || updateMutation.isPending}
                >
                  {editingId ? 'Save changes' : 'Create private booking'}
                </Button>
              </div>
            </form>
          )}
        </div>
      </div>

      <ModalShell
        isOpen={Boolean(invoiceModal)}
        onClose={() => setInvoiceModal(null)}
        labelledBy="private-invoice-title"
        panelClassName="max-w-md"
      >
        <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 p-5 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <h3
              id="private-invoice-title"
              className="text-lg font-semibold text-gray-900 dark:text-gray-50"
            >
              Generate invoice
            </h3>
            <button
              type="button"
              onClick={() => setInvoiceModal(null)}
              className="p-2 rounded-lg text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          {invoiceModal && (
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Invoice for{' '}
              <strong className="text-gray-900 dark:text-gray-100">{invoiceModal.guest_name}</strong>
              {invoiceModal.subtotal_amount != null && (
                <> — ${Number(invoiceModal.subtotal_amount).toFixed(2)}</>
              )}
            </p>
          )}
          <div className="space-y-4">
            <Input
              label="Due date"
              type="date"
              value={invoiceDueDate}
              onChange={(e) => setInvoiceDueDate(e.target.value)}
            />
            <Input
              label="Tax rate (%)"
              type="number"
              min="0"
              max="100"
              step="0.01"
              value={invoiceTaxRate}
              onChange={(e) => setInvoiceTaxRate(e.target.value)}
              placeholder="0"
            />
          </div>
          <div className="flex gap-2 mt-6 justify-end">
            <Button variant="outline" className="!rounded-xl" onClick={() => setInvoiceModal(null)}>
              Cancel
            </Button>
            <Button className="!rounded-xl" onClick={submitInvoice} loading={invoiceMutation.isPending}>
              Create invoice
            </Button>
          </div>
        </div>
      </ModalShell>
    </div>
  );
};

export default VendorPrivateBookings;
